import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ConsentInfoSheet } from '../ui/ConsentInfoSheet';
import { AnswerModeScreen } from './AnswerModeScreen';
import { ConnectPhoneScreen } from './ConnectPhoneScreen';
import { ActivationFallbackScreen, SwitchModeConfirmScreen } from './ActivationFallbackScreens';
import { ActivationReadyScreen } from './ActivationConfirmScreens';
import { useMobileActivation, MobileActivation } from '../../hooks/useMobileActivation';
import type { JourneyProgress, JourneyStart } from '../../types/forwarding';
interface MobileActivationJourneyProps {
  /** Resume or reactivate a returning user's saved setup. */
  start?: JourneyStart;
  /** Leave the journey from its first screen. */
  onExit?: () => void;
  /** Reported on every step so an interrupted setup can be resumed later. */
  onProgress?: (progress: JourneyProgress) => void;
  onComplete: (progress: JourneyProgress, destination: 'home' | 'callerExperience') => void;
  onContactSupport?: () => void;
}
const snapshot = (a: MobileActivation): JourneyProgress => ({
  mode: a.mode,
  rules: a.activeRules,
  setup: {
    carrierId: a.carrierId,
    os: a.os,
    androidBrand: a.androidBrand,
    method: a.method
  },
  draft: {
    screen: a.screen,
    method: a.method,
    codeStepIdx: a.codeStepIdx,
    settingsIdx: a.settingsIdx
  },
  verification: a.verification
});

/**
 * Mobile-number activation: when Vexu answers → how to connect (carrier code or
 * phone settings) → guided setup → recovery if needed → real test call.
 */
export function MobileActivationJourney({
  start,
  onExit,
  onProgress,
  onComplete,
  onContactSupport
}: MobileActivationJourneyProps) {
  const a = useMobileActivation(start);
  const [consentSheet, setConsentSheet] = useState(false);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;
  useEffect(() => {
    progressRef.current?.(snapshot(a));
  }, [a.screen, a.codeStepIdx, a.settingsIdx, a.mode, a.activeRules.join(), a.method, a.carrierId, a.os, JSON.stringify(a.verification)]);
  const finish = (destination: 'home' | 'callerExperience') => onComplete(snapshot(a), destination);
  const backFromConnect = () => a.entryScreen === 'method' ? onExit?.() : a.setScreen('mode');
  const renderScreen = () => {
    switch (a.screen) {
      case 'mode':
        return <AnswerModeScreen key="mode" a={a} onBack={onExit} onLearnMoreConsent={() => setConsentSheet(true)} data-id="element-217" data-mp="lf5kt71sqdkv" />;
      case 'fallback':
        return <ActivationFallbackScreen key="fallback" a={a} onContactSupport={onContactSupport} data-id="element-218" data-mp="1e3b5xuwri4l8" />;
      case 'switchConfirm':
        return <SwitchModeConfirmScreen key="switchConfirm" a={a} onCancel={() => a.setScreen(a.failedMethod ? 'fallback' : 'method')} data-id="element-219" data-mp="z50phstlydss" />;
      case 'ready':
        return <ActivationReadyScreen key="ready" a={a} onHome={() => finish('home')} onPersonalize={a.isReturning ? undefined : () => finish('callerExperience')} data-id="element-220" data-mp="3juyf9s6viuu1" />;
      // "Connect your phone": method choice, instructions and confirmation in one.
      default:
        return <ConnectPhoneScreen key="connect" a={a} onBack={backFromConnect} onContactSupport={onContactSupport} data-id="element-221" data-mp="2fyfk7skj6cz0" />;
    }
  };
  return <div className="absolute inset-0" data-id="element-222" data-mp="2bwkkgsi907nc">
      <AnimatePresence mode="wait" initial={false} data-id="element-223" data-mp="2bxlvwy36ew5l">
        {renderScreen()}
      </AnimatePresence>
      <ConsentInfoSheet open={consentSheet} onClose={() => setConsentSheet(false)} data-id="element-224" data-mp="1o7htgu1q52g6" />
    </div>;
}